import TextField from '@mui/material/TextField';
import { format } from '@react-input/mask';
import { phoneNumberValidator } from '@thesavingsgroup/constants/regex';
import { isEmpty, trim } from 'lodash';
import { ChangeEvent } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { inputEventHandler } from '../utils/inputEventHandler';

type PhoneNumberInputProps = {
  inputLabelKey: string;
  inputName: string;
  inputProps?: Record<string, unknown>;
  form: UseFormReturn<any>;
  validate?: (value: string) => string | boolean;
  value?: string | null;
};

export const PhoneNumberInput = ({
  inputLabelKey,
  inputName,
  inputProps,
  form,
  validate,
  value = '',
}: PhoneNumberInputProps) => {
  const {
    register,
    setValue,
    clearErrors,
    formState: { errors },
  } = form;

  const { t } = useTranslation();

  const defaultValue = inputEventHandler.phoneNumber.formatter(value);

  const phoneFieldValidation = {
    setValueAs: (value: string) =>
      trim(inputEventHandler.phoneNumber.parser(value)),
    required: t('validation.phone.required'),
    pattern: {
      value: phoneNumberValidator,
      message: t('validation.phone.invalid'),
    },
    ...(validate && { validate }),
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    // On some browsers (i.e. iOS Chrome) the autofill feature triggers the onChange event
    // at an unexpected time, causing the value to be set to the unformatted value. By using
    // setTimeout, we can ensure that the value is set to the formatted value on the next tick
    // (after the browser has finished autofilling the input). For this reason we rely on the
    // format utility function, rather than using the useMask hook.

    const formattedValue = format(event.target.value, {
      mask: '(___) ___-____',
      replacement: { _: /\d/ },
    });

    setTimeout(() => {
      const parsedValue = trim(
        inputEventHandler.phoneNumber.parser(formattedValue),
      );

      // Only validate if the phone number has enough digits (10 or more)
      const shouldValidate = parsedValue && parsedValue.length >= 10;

      setValue(inputName, formattedValue, {
        shouldDirty: true,
        shouldValidate,
      });

      // Clear errors if the phone number is valid and complete
      if (shouldValidate && phoneNumberValidator.test(parsedValue)) {
        clearErrors(inputName);
      }

      event.target.value = formattedValue;
    }, 0);
  };

  return (
    <TextField
      {...register(inputName, phoneFieldValidation)}
      autoComplete="tel-national"
      color="primary"
      defaultValue={defaultValue}
      error={!isEmpty(errors[inputName])}
      inputProps={{ ...inputProps, inputMode: 'numeric', maxLength: 14 }}
      label={t(inputLabelKey)}
      variant="outlined"
      fullWidth
      onChange={onChange}
    />
  );
};
